import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography, TextField, MenuItem, CircularProgress } from '@mui/material';
import ReusableModal from '../Modal/ReusableModal';
import projectService from '../../utils/apiRequests/projectService';
import useThemeStyles from '../../hooks/useThemeStyles';
import { PROJECT_TYPE_OPTIONS } from '../../utils/projectTypeConfig';

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'published', label: 'Published' },
];

const EditProjectModal = ({ open, onClose, project, onProjectUpdated }) => {
  const { colors } = useThemeStyles();
  const [title, setTitle] = useState('');
  const [type, setType] = useState('novel');
  const [status, setStatus] = useState('draft');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && project) {
      setTitle(project.title || '');
      setType(project.type || 'novel');
      setStatus(project.status || 'draft');
      setDescription(project.description || '');
      setError('');
    }
  }, [open, project]);

  const textFieldSx = {
    '& .MuiInputLabel-root': { color: colors.text.secondary },
    '& .MuiInputLabel-root.Mui-focused': { color: colors.interactive.primary },
    input: { color: colors.text.primary },
    textarea: { color: colors.text.primary },
    '& .MuiSelect-select': { color: colors.text.primary },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: colors.border.subtle },
      '&:hover fieldset': { borderColor: colors.interactive.primary },
      '&.Mui-focused fieldset': { borderColor: colors.interactive.primary },
    },
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Project title is required.');
      return;
    }
    setLoading(true);
    setError('');

    const response = await projectService.updateProject(project._id, {
      title: title.trim(),
      type,
      status,
      description,
    });

    setLoading(false);

    if (response.error) {
      setError(response.error?.response?.data?.error || 'Failed to update project.');
      return;
    }

    const updated = response.data?.data?.project || response.data?.project;
    if (onProjectUpdated) {
      onProjectUpdated(updated || { ...project, title: title.trim(), type, status, description });
    }
    onClose();
  };

  return (
    <ReusableModal
      open={open}
      onClose={onClose}
      title="Edit Project"
      width={440}
      ariaLabelledby="edit-project-modal-title"
      ariaDescribedby="edit-project-modal-description"
      showCloseButton
      contentSx={{ gap: 2 }}
    >
      {error && (
        <Typography sx={{ textAlign: 'center', color: colors.semantic.error }}>{error}</Typography>
      )}

      <TextField
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        fullWidth
        required
        disabled={loading}
        sx={textFieldSx}
      />
      <TextField
        select
        label="Type"
        value={type}
        onChange={(e) => setType(e.target.value)}
        fullWidth
        disabled={loading}
        sx={textFieldSx}
      >
        {PROJECT_TYPE_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Status"
        value={status}
        onChange={(e) => setStatus(e.target.value)}
        fullWidth
        disabled={loading}
        sx={textFieldSx}
      >
        {STATUS_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        fullWidth
        multiline
        minRows={2}
        maxRows={6}
        disabled={loading}
        sx={textFieldSx}
      />

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          sx={{
            width: '120px',
            color: colors.text.secondary,
            borderColor: colors.border.subtle,
            '&:hover': {
              color: colors.text.primary,
              borderColor: colors.border.primary,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={!title.trim() || loading}
          sx={{ width: '120px', px: 2.5, py: 0.75 }}
        >
          {loading ? <CircularProgress size={22} /> : 'Save'}
        </Button>
      </Box>
    </ReusableModal>
  );
};

EditProjectModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  project: PropTypes.shape({
    _id: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
    status: PropTypes.string,
    description: PropTypes.string,
  }),
  onProjectUpdated: PropTypes.func,
};

EditProjectModal.defaultProps = {
  project: null,
  onProjectUpdated: null,
};

export default EditProjectModal;
